// Voice input shared utilities: pause threshold, energy-based VAD, error mapping.
// All values here are tuned for the IDA chat composer; they are NOT a generic
// audio-processing toolkit and should not be exported beyond the voice module.

// Locked at 3 seconds per user decision 2026-05-16 (design doc §4.3).
// User feedback in beta may refine, but 3000 ms is the v1 default.
export const VOICE_INPUT_PAUSE_THRESHOLD_MS = 3000;

// Below this normalized RMS the audio frame is considered silence. The number
// is empirical: room noise on a typical laptop mic sits around 0.005 - 0.012;
// quiet speech opens around 0.025; 0.015 keeps a clear separation without
// missing soft consonants.
export const VOICE_INPUT_SILENCE_RMS = 0.015;

// Auto-cancel if the user starts the mic but never speaks. Keeps the WebSocket
// from being held open during accidental presses. The timeout returns silently
// to idle (johan ruling #125: no notice), leaving any pre-typed text untouched.
export const VOICE_INPUT_NO_SPEECH_TIMEOUT_MS = 10_000;

export type VoiceInputErrorCode =
  | "permission-denied"
  | "no-microphone"
  | "microphone-busy"
  | "token-mint-failed"
  | "speech-service-unavailable"
  | "unsupported-browser"
  | "unknown";

export interface VoiceInputError {
  code: VoiceInputErrorCode;
  message: string;
}

const PERMISSION_DENIED_MESSAGE =
  "Microphone access was blocked. Allow it in your browser's site settings to use voice input.";
const NO_MICROPHONE_MESSAGE = "No microphone was found on this device.";
const MICROPHONE_BUSY_MESSAGE =
  "The microphone is in use by another application. Close other apps and try again.";
const TOKEN_MINT_FAILED_MESSAGE =
  "Could not start voice input. Please try again in a moment.";
const SPEECH_UNAVAILABLE_MESSAGE =
  "The transcription service is temporarily unavailable.";
const UNSUPPORTED_BROWSER_MESSAGE =
  "Voice input is not supported in this browser.";
const UNKNOWN_MESSAGE = "Voice input failed unexpectedly.";

export function mapMediaError(error: unknown): VoiceInputError {
  if (typeof DOMException !== "undefined" && error instanceof DOMException) {
    switch (error.name) {
      case "NotAllowedError":
      case "SecurityError":
        return {
          code: "permission-denied",
          message: PERMISSION_DENIED_MESSAGE,
        };
      case "NotFoundError":
        return { code: "no-microphone", message: NO_MICROPHONE_MESSAGE };
      case "NotReadableError":
        return { code: "microphone-busy", message: MICROPHONE_BUSY_MESSAGE };
      default:
        return { code: "unknown", message: UNKNOWN_MESSAGE };
    }
  }
  return { code: "unknown", message: UNKNOWN_MESSAGE };
}

export function tokenMintError(): VoiceInputError {
  return { code: "token-mint-failed", message: TOKEN_MINT_FAILED_MESSAGE };
}

export function speechServiceError(): VoiceInputError {
  return {
    code: "speech-service-unavailable",
    message: SPEECH_UNAVAILABLE_MESSAGE,
  };
}

export function unsupportedBrowserError(): VoiceInputError {
  return { code: "unsupported-browser", message: UNSUPPORTED_BROWSER_MESSAGE };
}

// Browser capability check: MediaRecorder + AudioContext + getUserMedia.
// Used by VoiceInputButton to render a disabled tooltip rather than throw on click.
export function isVoiceInputSupported(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator;
  if (!nav?.mediaDevices?.getUserMedia) return false;
  if (typeof window.MediaRecorder === "undefined") return false;
  const AudioCtx =
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  return typeof AudioCtx === "function";
}

// Compute the root-mean-square amplitude of a Float32Array audio frame.
export function computeRms(samples: Float32Array): number {
  if (samples.length === 0) return 0;
  let sumSquares = 0;
  for (let i = 0; i < samples.length; i++) {
    const s = samples[i];
    sumSquares += s * s;
  }
  return Math.sqrt(sumSquares / samples.length);
}

// Down-mix a stereo Float32Array (interleaved L/R) to mono by averaging.
export function stereoToMono(samples: Float32Array): Float32Array {
  if (samples.length % 2 !== 0) return samples;
  const mono = new Float32Array(samples.length / 2);
  for (let i = 0, j = 0; i < samples.length; i += 2, j++) {
    mono[j] = (samples[i] + samples[i + 1]) / 2;
  }
  return mono;
}

// Float32 (-1..1) → Int16 little-endian PCM. Azure Speech expects 16-bit PCM
// frames when streaming audio over the conversation-transcription WebSocket.
export function float32ToInt16Pcm(samples: Float32Array): Int16Array {
  const out = new Int16Array(samples.length);
  for (let i = 0; i < samples.length; i++) {
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    out[i] = clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff;
  }
  return out;
}
